import type { Fixture } from "./types.js";

/**
 * Team tie. Two teams of n, each seeded 1..n.
 *
 * **Mirrored** (the default): every fixture is a pair `{i, j}` from one team against the
 * identical pair from the other, so both sides of every match have the same seed sum and
 * there is no draw luck at all.
 *
 * **Open**: any pair can meet any pair whose seed sum is within 2 of its own. It gives up the
 * counterpart duels for varied opponents, and lifts the cap from n − 1 matches each to
 * 2(n − 1), because a partnership may then play twice.
 *
 * Full format spec, including why anyone would want this: docs/formats/team-tie.md.
 */

export type TiePairing = "mirrored" | "open";

/** How far apart two pairs' seed sums may be in an Open fixture. */
export const OPEN_SEED_GAP = 2;

/**
 * The values of `m` — matches per player — that a roster of `n` can actually run.
 *
 * Two hard constraints, and the second is the one that surprises people: `n × m` has to be
 * even, because every fixture uses two slots. An odd roster therefore forces an even `m`.
 * Five a side can play 2 each or 4 each and never 3, and an organiser typing 3 deserves to
 * be shown the neighbours rather than an error about parity.
 */
export function validMatchesPerPlayer(n: number, pairing: TiePairing = "mirrored"): number[] {
  // Open lets every partnership play twice, so twice the teammates is the ceiling.
  const most = pairing === "open" ? 2 * (n - 1) : n - 1;
  const valid: number[] = [];
  for (let m = 1; m <= most; m++) if ((n * m) % 2 === 0) valid.push(m);
  return valid;
}

export function fixtureCount(n: number, m: number): number {
  return (n * m) / 2;
}

/**
 * Which pairs play, as 1-based seed pairs.
 *
 * Choosing the pairs is exactly the problem of building an **m-regular graph on n
 * vertices** — every player needs degree m, and each edge is one fixture. Circulant graphs
 * give that directly: lay the seeds out on a ring and join each to the one `o` places
 * along, for every offset up to `m / 2`. Each offset contributes 2 to every vertex's degree,
 * so `floor(m / 2)` offsets get everyone to an even m. An odd m needs one more perfect
 * matching across the ring's diameter, which is why an odd m requires an even n.
 *
 * The ring layout also buys partner fairness for nothing: because seeds sit in order around
 * it, the wraparound means the top seed partners both the seeds just below them and the
 * seeds at the bottom, so partner-strength evens out across the roster. At `m = n - 1` the
 * whole thing degenerates to the complete graph, so an all-play-all tie is not a special
 * case — it is just the maximum.
 */
export function seedPairs(n: number, m: number): [number, number][] {
  if (n < 2) throw new Error("a team tie needs at least two players a side");
  if (m > n - 1) {
    throw new Error(`each player can partner at most ${n - 1} teammates, not ${m}`);
  }
  if (m < 1) throw new Error("every player must play at least one match");
  if ((n * m) % 2 !== 0) {
    const options = validMatchesPerPlayer(n).join(" or ");
    throw new Error(`${n} a side cannot play ${m} matches each — try ${options}`);
  }

  const pairs: [number, number][] = [];
  const rings = Math.floor(m / 2);

  for (let offset = 1; offset <= rings; offset++) {
    for (let i = 0; i < n; i++) pairs.push([i + 1, ((i + offset) % n) + 1]);
  }

  // The diameter matching, for odd m. `n × m` being even already guarantees n is even here.
  if (m % 2 === 1) {
    for (let i = 0; i < n / 2; i++) pairs.push([i + 1, i + n / 2 + 1]);
  }

  return pairs.map(([a, b]) => (a <= b ? [a, b] : [b, a]));
}

/**
 * An Open tie's partnerships, which may run past n − 1.
 *
 * Up to n − 1 this is `seedPairs` exactly, so nobody partners the same teammate twice. Past
 * it, every partnership plays once — the complete graph — and `seedPairs` supplies the
 * remainder, so each partnership plays at most twice. `n(n − 1)` is always even, which makes
 * the remainder feasible exactly when `n × m` is.
 */
export function openSeedPairs(n: number, m: number): [number, number][] {
  if (n < 2) throw new Error("a team tie needs at least two players a side");
  if (m > 2 * (n - 1)) {
    throw new Error(`each player can partner each of ${n - 1} teammates at most twice, not ${m}`);
  }
  if (m <= n - 1) return seedPairs(n, m);

  const pairs: [number, number][] = [];
  for (let a = 1; a <= n; a++) for (let b = a + 1; b <= n; b++) pairs.push([a, b]);
  if (m === n - 1) return pairs;
  return [...pairs, ...seedPairs(n, m - (n - 1))];
}

/**
 * Which side-B partnership each side-A partnership plays, for an Open tie.
 *
 * Both sides bring the same list of partnerships. Starting from the identity — the Mirrored
 * draw, every gap 0 — swap side B's assignments pairwise, in a fixed order, keeping any swap
 * that lowers the cost, until a whole pass changes nothing. The cost is compared in order:
 *
 *   1. fixtures whose seed sums differ by more than `OPEN_SEED_GAP` (must end at 0, and the
 *      identity already has 0, so it can only stay there)
 *   2. the same two pairs meeting more than once
 *   3. the most times any two opposing players meet
 *   4. fixtures that share a player with every other fixture, which no order can rest
 *   5. the sum of squared meeting counts, so the rest spread out too
 *   6. fixtures that are still a seed pair against the identical pair
 *
 * No randomness: the preview has to be the draw that gets saved. Swaps alone meet the spec's
 * bound — no two opponents meeting more than ⌈m/2⌉ + 1 times — for every shape from 2 to 10
 * a side, which `team-tie.test.ts` sweeps.
 */
export function openAssignment(n: number, pairs: [number, number][]): [number, number][] {
  const total = pairs.length;
  const assigned = pairs.map((_, i) => i);
  const sum = (pair: [number, number]) => pair[0] + pair[1];

  const cost = (): number[] => {
    const meet = Array.from({ length: (n + 1) * (n + 1) }, () => 0);
    const matchups = new Map<number, number>();
    let wide = 0;
    let same = 0;
    for (let i = 0; i < total; i++) {
      const a = pairs[i] as [number, number];
      const b = pairs[assigned[i] as number] as [number, number];
      if (Math.abs(sum(a) - sum(b)) > OPEN_SEED_GAP) wide += 1;
      if (a[0] === b[0] && a[1] === b[1]) same += 1;
      for (const x of a) for (const y of b) meet[x * (n + 1) + y]! += 1;
      const key = ((a[0] * (n + 1) + a[1]) * (n + 1) + b[0]) * (n + 1) + b[1];
      matchups.set(key, (matchups.get(key) ?? 0) + 1);
    }
    let most = 0;
    let squares = 0;
    for (const count of meet) {
      if (count > most) most = count;
      squares += count * count;
    }
    let repeats = 0;
    for (const count of matchups.values()) repeats += count - 1;

    // Fixtures with nobody in common with any other fixture can only ever be played straight
    // after someone's last match, so no order can rest them. Counting them here, rather than
    // ordering inside the search, is what gives `orderForRest` a fixture list it can space.
    let stranded = 0;
    for (let i = 0; i < total; i++) {
      const a = pairs[i] as [number, number];
      const b = pairs[assigned[i] as number] as [number, number];
      let apart = false;
      for (let j = 0; j < total && !apart; j++) {
        if (j === i) continue;
        const c = pairs[j] as [number, number];
        const d = pairs[assigned[j] as number] as [number, number];
        apart = !c.some((x) => a.includes(x)) && !d.some((y) => b.includes(y));
      }
      if (!apart) stranded += 1;
    }
    return [wide, repeats, most, stranded, squares, same];
  };

  const lower = (a: number[], b: number[]): boolean => {
    for (let i = 0; i < a.length; i++) {
      if (a[i] !== b[i]) return (a[i] as number) < (b[i] as number);
    }
    return false;
  };

  const swap = (i: number, j: number) => {
    const held = assigned[i] as number;
    assigned[i] = assigned[j] as number;
    assigned[j] = held;
  };

  let current = cost();
  let improved = true;
  while (improved) {
    improved = false;
    for (let i = 0; i < total; i++) {
      for (let j = i + 1; j < total; j++) {
        swap(i, j);
        const next = cost();
        if (lower(next, current)) {
          current = next;
          improved = true;
        } else {
          swap(i, j);
        }
      }
    }
  }

  return assigned.map((index) => pairs[index] as [number, number]);
}

export interface OrderResult<T> {
  order: T[];
  /**
   * Fixtures placed where a player is still resting. Zero is the goal and is usually
   * reachable; it stops being reachable as m approaches n − 1, because by then almost every
   * fixture shares a player with almost every other.
   */
  violations: number;
}

/** Whether two fixtures put anybody on court in both. */
function shares(a: readonly (number | string)[], b: readonly (number | string)[]): boolean {
  return a.some((key) => b.includes(key));
}

/**
 * Order the fixtures so nobody plays back to back.
 *
 * Selecting the pairs is the easy half of this format; ordering them is where it is won or
 * lost. A naive order has one player on court three times running while another sits for an
 * hour, and that is the complaint an organiser actually gets.
 *
 * Each fixture is the list of who is on court in it. A Mirrored tie passes its seed pair,
 * because seed i on both sides is always on court together; an Open tie passes all four
 * players, because there the two sides differ.
 *
 * Depth-first with backtracking, preferring at each step the fixture whose players have
 * rested longest. That is ample at tie sizes — a 10-a-side Open tie is 90 fixtures at most —
 * and the node budget only exists so a pathological input degrades to a greedy answer
 * instead of hanging. When no clean order exists the greedy fallback reports how many
 * violations it settled for, rather than pretending.
 *
 * `window` is how many consecutive fixtures must not repeat a player: 2 for a single court,
 * meaning simply no back-to-back. On c courts, matches go out c at a time, so a player must
 * not appear twice within c consecutive fixtures — they cannot be on two courts at once —
 * and one more keeps them off the very next batch as well.
 */
export function orderForRest<T extends readonly (number | string)[]>(
  fixtures: T[],
  window = 2,
): OrderResult<T> {
  const total = fixtures.length;
  if (total === 0) return { order: [], violations: 0 };

  const used = new Array<boolean>(total).fill(false);
  const chosen: number[] = [];
  let budget = 200_000;

  const clashes = (index: number, at: number): boolean => {
    const players = fixtures[index] as T;
    for (let i = Math.max(0, at - (window - 1)); i < at; i++) {
      if (shares(fixtures[chosen[i] as number] as T, players)) return true;
    }
    return false;
  };

  /** How long every one of this fixture's players has been off court. Bigger is fresher. */
  const rest = (index: number): number => {
    let freshest = chosen.length;
    for (const key of fixtures[index] as T) {
      let gap = chosen.length;
      for (let i = chosen.length - 1; i >= 0; i--) {
        if ((fixtures[chosen[i] as number] as T).includes(key)) {
          gap = chosen.length - i;
          break;
        }
      }
      freshest = Math.min(freshest, gap);
    }
    return freshest;
  };

  const search = (): boolean => {
    if (chosen.length === total) return true;
    if (budget-- <= 0) return false;

    const candidates: number[] = [];
    for (let i = 0; i < total; i++) {
      if (!used[i] && !clashes(i, chosen.length)) candidates.push(i);
    }
    candidates.sort((a, b) => rest(b) - rest(a));

    for (const candidate of candidates) {
      used[candidate] = true;
      chosen.push(candidate);
      if (search()) return true;
      chosen.pop();
      used[candidate] = false;
    }
    return false;
  };

  if (search()) {
    return { order: chosen.map((i) => fixtures[i] as T), violations: 0 };
  }

  // No clean order, or the budget ran out. Take the least bad one greedily and say so.
  chosen.length = 0;
  used.fill(false);
  let violations = 0;
  for (let step = 0; step < total; step++) {
    let best = -1;
    let bestScore = -Infinity;
    for (let i = 0; i < total; i++) {
      if (used[i]) continue;
      const score = (clashes(i, step) ? -1_000_000 : 0) + rest(i);
      if (score > bestScore) {
        bestScore = score;
        best = i;
      }
    }
    if (clashes(best, step)) violations += 1;
    used[best] = true;
    chosen.push(best);
  }

  return { order: chosen.map((i) => fixtures[i] as T), violations };
}

export interface TeamTieOptions {
  /** Matches each player gets. Must be one of `validMatchesPerPlayer(n, pairing)`. */
  matchesPerPlayer: number;
  /** Courts running at once. The clubhouse has one, which means no back-to-back matches. */
  courts?: number;
  /**
   * Pin the `{1, 2}` fixture last so the tie finishes on the top seeds, accepting a rest
   * violation if that is what it costs. Off by default: spacing is worth more than theatre
   * on a Tuesday, and the organiser can ask for the finale when it is a final.
   *
   * In an Open tie this means `{1, 2}` against `{1, 2}`, which the draw may not contain.
   */
  topSeedsLast?: boolean;
  /** Mirrored unless asked. */
  pairing?: TiePairing;
}

export interface TeamTie {
  fixtures: Fixture[];
  /**
   * How many fixtures start while one of their players is still on court from the one
   * before. Reported rather than swallowed, because for some shapes zero is not achievable
   * and the organiser who will be asked "why am I on again" should hear it first.
   *
   * The unavoidable case is real rather than theoretical. At `m = n - 1` the fixture set is
   * the complete graph, where each fixture shares nobody with exactly one other — four a
   * side playing three each is six fixtures among which only three disjoint pairings exist,
   * so no ordering keeps everyone apart and the honest answer is 2.
   */
  restViolations: number;
  /** The widest difference in seed sums across any fixture. Always 0 when Mirrored. */
  largestSeedGap: number;
}

/** One fixture before it is ordered: side A's seed pair against side B's. */
interface Pairing {
  a: [number, number];
  b: [number, number];
}

/**
 * The whole tie: which pairs play, in the order they should be called.
 *
 * `teamA` and `teamB` are member ids **in seed order** — index 0 is seed 1. They must be the
 * same length; a tie of unequal sides has no meaning in either pairing, and silently
 * truncating the longer roster would drop a player who turned up.
 */
export function teamTieDraw(teamA: string[], teamB: string[], options: TeamTieOptions): TeamTie {
  if (teamA.length !== teamB.length) {
    throw new Error(`a team tie needs equal rosters, got ${teamA.length} and ${teamB.length}`);
  }

  const n = teamA.length;
  const open = options.pairing === "open";
  const window = Math.max(2, (options.courts ?? 1) + 1);

  const pairings: Pairing[] = open
    ? (() => {
        const pairs = openSeedPairs(n, options.matchesPerPlayer);
        const against = openAssignment(n, pairs);
        return pairs.map((a, i) => ({ a, b: against[i] as [number, number] }));
      })()
    : seedPairs(n, options.matchesPerPlayer).map((pair) => ({ a: pair, b: pair }));

  // Who is on court, as ordering keys. Mirrored keeps the seed pair, which is what it always
  // ordered on; Open needs all four, because there side A's seed 2 is not side B's.
  const keysOf = (p: Pairing): (number | string)[] =>
    open ? [`A${p.a[0]}`, `A${p.a[1]}`, `B${p.b[0]}`, `B${p.b[1]}`] : [...p.a];
  const keyed = pairings.map((p) => Object.assign(keysOf(p), { pairing: p }));

  const rested = orderForRest(keyed, window);
  let ordered = rested.order.map((keys) => keys.pairing);
  let restViolations = rested.violations;

  if (options.topSeedsLast) {
    const isTop = (p: Pairing) => p.a[0] === 1 && p.a[1] === 2 && p.b[0] === 1 && p.b[1] === 2;
    const finale = ordered.findIndex(isTop);
    // Absent when m is low enough that seeds 1 and 2 never partner, or when an Open draw
    // never put them against each other. Nothing to pin, and demanding it would fail a tie
    // that is otherwise perfectly well formed.
    if (finale !== -1) {
      const [pinned] = ordered.splice(finale, 1);
      ordered = [...ordered, pinned as Pairing];
      // Moving a fixture to the end can create a clash on either side of where it left and
      // where it landed, so the count from before the move no longer describes this order.
      restViolations = countViolations(ordered.map(keysOf), window);
    }
  }

  const fixtures: Fixture[] = ordered.map(({ a, b }, index) => ({
    round: 1,
    sequence: index + 1,
    seedPair: a,
    // Only where it differs. A Mirrored fixture reads exactly as it always has.
    ...(open ? { seedPairB: b } : {}),
    sideA: {
      kind: "players",
      memberIds: [teamA[a[0] - 1] as string, teamA[a[1] - 1] as string],
    },
    sideB: {
      kind: "players",
      memberIds: [teamB[b[0] - 1] as string, teamB[b[1] - 1] as string],
    },
  }));

  const largestSeedGap = ordered.reduce(
    (widest, { a, b }) => Math.max(widest, Math.abs(a[0] + a[1] - b[0] - b[1])),
    0,
  );

  return { fixtures, restViolations, largestSeedGap };
}

/** Fixtures whose players are still resting from one of the previous `window - 1`. */
function countViolations(order: (number | string)[][], window: number): number {
  let violations = 0;
  for (let i = 1; i < order.length; i++) {
    for (let j = Math.max(0, i - (window - 1)); j < i; j++) {
      if (shares(order[j] as (number | string)[], order[i] as (number | string)[])) {
        violations += 1;
        break;
      }
    }
  }
  return violations;
}
